import { Menu, Select } from 'antd';
import i18n from 'i18next';
import { groupBy, partition } from 'lodash';

import { type TTaskTemplatesChooserType } from './types';

type TTemplateItem = {
  __typename?: 'TaskTemplate' | undefined;
  id: any;
  name: string;
  global?: boolean | null;
  workContainerType: { __typename?: 'WorkContainerType' | undefined; id: any; name: string };
};

// Builds flat groups with a category prefix in the label, e.g. "SkySpecs Standard Templates – Repair Task".
// `categoryKey` is a stable, untranslated identifier used for React keys; `categoryLabel` is the
// translated, user-facing display string used in the group title.
function buildFlatTypeGroups(
  templates: TTemplateItem[],
  displayType: TTaskTemplatesChooserType,
  categoryKey: string,
  categoryLabel: string
) {
  const grouped = groupBy(templates, 'workContainerType.name');
  return Object.keys(grouped).map(typeName => {
    const groupLabel = i18n.t('taskTemplates.categoryTypeGroup', '{{category}} – {{type}}', {
      category: categoryLabel,
      type: typeName,
    });
    return displayType === 'menu' ? (
      <Menu.ItemGroup key={`${categoryKey}_${typeName}`} title={groupLabel}>
        {grouped[typeName].map(template => (
          <Menu.Item key={`${typeName}_${template.id}`}>{template.name}</Menu.Item>
        ))}
      </Menu.ItemGroup>
    ) : (
      <Select.OptGroup key={`${categoryKey}_${typeName}`} label={groupLabel}>
        {grouped[typeName].map(template => (
          <Select.Option key={`${typeName}_${template.id}`} value={template.id}>
            {template.name}
          </Select.Option>
        ))}
      </Select.OptGroup>
    );
  });
}

export function formatTemplatesMenu(
  templatesData: TTemplateItem[],
  displayType: TTaskTemplatesChooserType
) {
  const [globalTemplates, customTemplates] = partition(templatesData, ['global', true]);
  const result: JSX.Element[] = [];
  // Single-level grouping, by work container type AND global/custom status
  if (globalTemplates.length > 0) {
    result.push(
      ...buildFlatTypeGroups(
        globalTemplates,
        displayType,
        'SkySpecs Standard Templates',
        i18n.t('taskTemplates.skySpecsStandardTemplates', 'SkySpecs Standard Templates')
      )
    );
  }

  // Append custom templates, if applicable
  if (customTemplates.length > 0) {
    result.push(
      ...buildFlatTypeGroups(
        customTemplates,
        displayType,
        'Custom Templates',
        i18n.t('taskTemplates.customTemplates', 'Custom Templates')
      )
    );
  }
  return result;
}
